import React, { useEffect, useRef, useState, useMemo, forwardRef } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import styles from './PlanTable.module.css';
import { AutoSizer, List } from 'react-virtualized';
import { AuthChecker, Button } from '../../atomicComponents';
import { LinearProgress, Popover } from '@material-ui/core';
import { columnsPropType } from '../../types';
import config from "../../config/config";
import { APPLICATION_VARIANTS } from "../../constants";
import { SelectionMenu, TableHead, TableRow } from '../../atomicComponents';

const SummaryRow = forwardRef(({ style, children }, ref) => (
  <div style={style} ref={ref} className={styles.summaryContainer}>
    <div className={styles.summary}>{children}</div>
  </div>
));

const PlanTable = ({
  rows: data,
  columns: originalColumns,
  dateColumns = [],
  classNames = {},
  rowHeight = 38,
  headerHeight,
  defaultColumns: originalDefaultColumns,
  primaryAction,
  secondaryActions = [],
  setHasActiveEdits,
  setCount,
  aggregateSequenceDate,
  isDataLoading,
  isEmergencyAgSnDisabled,
}) => {
  const [rowsData, setRowsData] = useState({
    data: [...data],
    filteredData: [...data],
  });

  const leftListRef = useRef(null);
  const rightListRef = useRef(null);
  const leftTableHeadRef = useRef(null);
  const rightTableHeadRef = useRef(null);
  const leftSummaryRowRef = useRef(null);
  const rightSummaryRowRef = useRef(null);

  const handleListScroll = (e, tableHeadRef) =>
    (tableHeadRef.current.scrollLeft = e.target.scrollLeft);

  const syncListToTableHead = (e, listRef) =>
    (listRef.current.scrollLeft = e.target.scrollLeft);

  const [fieldToSearchTextMap, setFieldToSearchTextMap] = useState({});
  const [isEllipsisPopupOpen, setIsEllipsisPopupOpen] = useState(false);
  const [ellipsisAnchorEl, setEllipsisAnchorEl] = useState(null);
  const [ellipsisPopupValue, setEllipsisPopupValue] = useState(null);
  const [fieldToFiltersSetMap, setFieldToFiltersSetMap] = useState({});

  const [monthlyTarget, setMonthlyTarget] = useState(0);
  const [monthlyBalance, setMonthlyBalance] = useState(0);
  const [totalDayWiseQuantities, setTotalDayWiseQuantities] = useState({});

  const closeEllipsisPopup = () => setIsEllipsisPopupOpen(false);

  const handlePopoverOpen = (event, val) => {
    if (event.currentTarget.scrollWidth > event.currentTarget.clientWidth) {
      setEllipsisPopupValue(val);
      setIsEllipsisPopupOpen(true);
      setEllipsisAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setIsEllipsisPopupOpen(false);
    setEllipsisAnchorEl(null);
  };

  const defaultColumns = useMemo(
    () =>
      [
        {
          title: '#',
          field: '_serialNumber',
          width: 54,
          render: (_, idx) => idx || null,
          renderHeader: rows => (
            <div className={styles.asnsCount} data-testid="count">
              {rows?.length || 0}
            </div>
          ),
          headerStyles: {
            paddingLeft: 7,
            paddingRight: 2,
          },
        },
        ...originalDefaultColumns,
      ].filter(i => i),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [originalDefaultColumns],
  );
  const [selectedColumns, setSelectedColumns] = useState(defaultColumns);

  const noOfFixedColumns = 1;

  const [isCustomizeTableMenuOpen, setIsCustomizeTableMenuOpen] = useState(
    false,
  );
  const [showCopyAlert, setShowCopyAlert] = useState(false);

  const customizeTableButtonRef = useRef(null);

  const reorder = (list, startIndex, endIndex) => {
    const result = [...list];
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const reorderHeaders = result => {
    // dropped outside the list or dropped at serial number place
    if (!result.destination || result.destination.index < noOfFixedColumns) {
      return;
    }

    const reorderedColumns = reorder(
      selectedColumns,
      result.source.index,
      result.destination.index,
    );

    setSelectedColumns(reorderedColumns);
  };

  const clearAllListeners = () => {
    if (leftListRef)
      leftListRef.current.removeEventListener('scroll', () => {});
    if (rightListRef)
      leftListRef.current.removeEventListener('scroll', () => {});
    leftTableHeadRef.current.removeEventListener('scroll', () => {});
    rightTableHeadRef.current.removeEventListener('scroll', () => {});
  };

  const calculateMonthlyTarget = () => {
    setMonthlyTarget(
      rowsData.filteredData.reduce((acc, vc) => acc + vc.target, 0),
    );
  };

  const calculateMonthlyBalance = () => {
    setMonthlyBalance(
      rowsData.filteredData.reduce((acc, vc) => acc + vc.balance, 0),
    );
  };

  const calculateTotalDayWiseQuantities = () => {
    const totalRow = {};
    dateColumns.forEach(
      dateCol => (totalRow[dateCol.field] = { quantity: null }),
    );
    rowsData.filteredData.forEach(row => {
      dateColumns.forEach(dateCol => {
        if (row[dateCol.field]?.quantity) {
          totalRow[dateCol.field].quantity += row[dateCol.field].quantity;
        }
      });
    });

    setTotalDayWiseQuantities(totalRow);
  };

  const rowRenderer = ({
    style,
    index,
    key,
    columns,
    tableRowClassNames = {},
  }) => {
    const handleCopy = text => {
      navigator.clipboard.writeText(text);
      setShowCopyAlert(true);
    };

    return (
      <TableRow
        index={index}
        style={style}
        key={key}
        row={rowsData.filteredData[index]}
        columns={columns}
        classNames={{ ...classNames, ...tableRowClassNames }}
        rowHeight={rowHeight}
        onCopy={handleCopy}
        handlePopoverOpen={handlePopoverOpen}
        handlePopoverClose={handlePopoverClose}
      />
    );
  };

  const handleColumnSelectionChange = columnTitle => {
    if (selectedColumns.map(({ title }) => title).includes(columnTitle))
      return setSelectedColumns(prev =>
        prev.filter(column => column.title !== columnTitle),
      );
    return setSelectedColumns(prev => [
      ...prev,
      ...originalColumns.filter(({ title }) => title === columnTitle),
    ]);
  };

  const applyColumnsSelections = columnSelections => {
    setSelectedColumns([
      ...defaultColumns.slice(
        0,
        defaultColumns.length - originalDefaultColumns.length,
      ),
      ...originalColumns.filter(col => columnSelections.includes(col.title)),
    ]);
  };

  useEffect(() => {
    setRowsData({
      data: [...data],
      filteredData: [...data],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    leftListRef.current = document.getElementById('left-table-id');
    rightListRef.current = document.getElementById('right-table-id');

    if (leftListRef.current) {
      leftListRef.current.addEventListener('scroll', e =>
        handleListScroll(e, leftTableHeadRef),
      );
      leftListRef.current.addEventListener('scroll', e =>
        handleListScroll(e, leftSummaryRowRef),
      );
    }
    if (rightListRef.current) {
      rightListRef.current.addEventListener('scroll', e =>
        handleListScroll(e, rightTableHeadRef),
      );
      rightListRef.current.addEventListener('scroll', e =>
        handleListScroll(e, rightSummaryRowRef),
      );
    }
    leftTableHeadRef.current.addEventListener('scroll', e =>
      syncListToTableHead(e, leftListRef),
    );
    rightTableHeadRef.current.addEventListener('scroll', e =>
      syncListToTableHead(e, rightListRef),
    );

    return clearAllListeners;
  }, []);

  useEffect(() => {
    if (setHasActiveEdits) {
      const areEditsActive = rowsData.data.some(row => row.isSelected);
      setHasActiveEdits(areEditsActive);
    }
  }, [rowsData.data, setHasActiveEdits]);

  useEffect(() => {
    setCount(rowsData.filteredData.length);
    calculateMonthlyTarget();
    calculateMonthlyBalance();
    calculateTotalDayWiseQuantities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsData.filteredData]);

  useEffect(() => {
    if (showCopyAlert) setShowCopyAlert(false);
  }, [showCopyAlert]);

  useDeepCompareColumnArraysEffect(() => {
    setSelectedColumns(defaultColumns);
  }, [defaultColumns]);

  useEffect(() => {
    setRowsData(prevState => {
      const dataFilteredBySearchTexts = filterBySearchTexts(
        fieldToSearchTextMap,
        prevState.data,
        selectedColumns,
      );

      const dataFilteredBySearchAndFilters = filterBySelectedFilters(
        fieldToFiltersSetMap,
        dataFilteredBySearchTexts,
        selectedColumns,
      );

      return { ...prevState, filteredData: dataFilteredBySearchAndFilters };
    });
  }, [
    selectedColumns,
    fieldToFiltersSetMap,
    fieldToSearchTextMap,
    rowsData.data,
  ]);

  return (
    <>
      <div
        style={{ width: '100%', height: '100%' }}
        className={clsx(
          styles.baseContainer,
          classNames.tableContainerClassName,
        )}
      >
        <div
          style={{ width: '100%' }}
          className={clsx(
            styles.basicTableContainer,
            classNames.tableContainerClassName,
          )}
        >
          <AutoSizer>
            {({ width: windowWidth, height: windowHeight }) => {
              const width = windowWidth;
              const height = windowHeight;
              const listHeight = height - 2 * rowHeight;
              const leftTableWidth = (width / 100) * 70;
              const rightTableWidth = (width / 100) * 30;

              return (
                <div
                  style={{
                    height,
                    width,
                  }}
                >
                  <div
                    className={styles.basicTable}
                    style={{
                      height,
                      width: leftTableWidth,
                    }}
                  >
                    <TableHead
                      columns={selectedColumns}
                      ref={leftTableHeadRef}
                      classNames={{ tableHeaderClassName: styles.noBoxShadow }}
                      height={headerHeight || rowHeight}
                      reorderHeaders={reorderHeaders}
                      rows={rowsData.filteredData}
                      allRows={rowsData.data}
                      fieldToSearchTextMap={fieldToSearchTextMap}
                      setSearchTextForTitleCurry={field => value =>
                        setFieldToSearchTextMap(prev => ({
                          ...prev,
                          [field]: value.toLowerCase(),
                        }))}
                      noOfFixedColumns={noOfFixedColumns}
                      fieldToFiltersSetMap={fieldToFiltersSetMap}
                      setFiltersForFieldCurry={field => selectedFilters =>
                        setFieldToFiltersSetMap(prev => ({
                          ...prev,
                          [field]: new Set(selectedFilters),
                        }))}
                    />
                    <List
                      ref={leftListRef}
                      id="left-table-id"
                      onScroll={({ scrollTop }) => {
                        if (rightListRef.current)
                          rightListRef.current.scrollTop = scrollTop;
                      }}
                      containerStyle={{ overflow: 'initial' }}
                      rowRenderer={listProps =>
                        rowRenderer({
                          ...listProps,
                          columns: selectedColumns,
                        })
                      }
                      height={listHeight}
                      rowHeight={rowHeight}
                      rowCount={rowsData.filteredData.length}
                      width={(width / 100) * 70}
                      className={clsx(
                        styles.scrollBar,
                        styles.heightAdjustedListScroll,
                      )}
                      overscanRowCount={10}
                    />
                    <SummaryRow
                      style={{
                        height: rowHeight,
                        width: leftTableWidth,
                      }}
                      ref={leftSummaryRowRef}
                    >
                      {selectedColumns.map((col, index) => {
                        if (index === noOfFixedColumns)
                          return (
                            <div
                              style={{
                                width: col.width,
                                display: 'inline-block',
                              }}
                            >
                              Grand Total
                            </div>
                          );
                        if (col.field === 'target')
                          return (
                            <div
                              style={{
                                width: col.width,
                                display: 'inline-flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <div style={{ fontSize: 11 }}>Monthly Target</div>
                              <div>{monthlyTarget}</div>
                            </div>
                          );
                        if (col.field === 'balance')
                          return (
                            <div
                              style={{
                                width: col.width,
                                display: 'inline-flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <div style={{ fontSize: 11 }}>
                                Monthly Balance
                              </div>
                              <div>{monthlyBalance}</div>
                            </div>
                          );
                        return (
                          <div
                            style={{
                              width: col.width,
                              display: 'inline-block',
                            }}
                          />
                        );
                      })}
                    </SummaryRow>
                  </div>

                  <div
                    className={styles.basicTable}
                    style={{
                      height,
                      width: rightTableWidth,
                    }}
                  >
                    <TableHead
                      columns={dateColumns}
                      ref={rightTableHeadRef}
                      classNames={{
                        tableHeaderClassName: clsx(
                          styles.noPadding,
                          styles.noBoxShadow,
                        ),
                        tableHeadCellClassName: styles.noPadding,
                      }}
                      height={headerHeight || rowHeight}
                      reorderHeaders={reorderHeaders}
                      rows={rowsData.filteredData}
                      allRows={rowsData.data}
                      fieldToSearchTextMap={fieldToSearchTextMap}
                      setSearchTextForTitleCurry={field => value =>
                        setFieldToSearchTextMap(prev => ({
                          ...prev,
                          [field]: value.toLowerCase(),
                        }))}
                      noOfFixedColumns={noOfFixedColumns}
                      fieldToFiltersSetMap={fieldToFiltersSetMap}
                      setFiltersForFieldCurry={field => selectedFilters =>
                        setFieldToFiltersSetMap(prev => ({
                          ...prev,
                          [field]: new Set(selectedFilters),
                        }))}
                      isDragDisabled
                    />
                    <List
                      ref={rightListRef}
                      id="right-table-id"
                      onScroll={({ scrollTop }) => {
                        if (leftListRef.current)
                          leftListRef.current.scrollTop = scrollTop;
                      }}
                      containerStyle={{ overflow: 'initial' }}
                      rowRenderer={listProps =>
                        rowRenderer({
                          ...listProps,
                          columns: dateColumns,
                          tableRowClassNames: {
                            tableCellClassName: styles.noPadding,
                          },
                        })
                      }
                      height={listHeight}
                      rowHeight={rowHeight}
                      rowCount={rowsData.filteredData.length}
                      width={rightTableWidth}
                      className={clsx(
                        styles.scrollBar,
                        styles.heightAdjustedListScroll,
                        styles.dateColumns,
                      )}
                      overscanRowCount={10}
                    />
                    <SummaryRow
                      style={{
                        height: rowHeight,
                        width: rightTableWidth,
                      }}
                      ref={rightSummaryRowRef}
                    >
                      {dateColumns.map(dateCol => (
                        <div
                          style={{
                            height: '100%',
                            display: 'inline-flex',
                            alignItems: 'center',
                            width: dateCol.width,
                            justifyContent: 'flex-start',
                          }}
                        >
                          {dateCol.render(totalDayWiseQuantities)}
                        </div>
                      ))}
                    </SummaryRow>
                  </div>
                  {isDataLoading && (
                    <div>
                      <LinearProgress />
                    </div>
                  )}
                </div>
              );
            }}
          </AutoSizer>
        </div>
        <div className={styles.actionsBar}>
          <Button
            className={styles.actionButton}
            variant="tertiary"
            onClick={() => setIsCustomizeTableMenuOpen(true)}
            ref={customizeTableButtonRef}
          >
            CUSTOMIZE TABLE
          </Button>
          {secondaryActions.map(action => (
            <AuthChecker operation={action.authOperation}>
              {isAuthorized => {
                if (!action.component)
                  return (
                    <Button
                      variant="primary"
                      className={styles.actionButton}
                      style={{
                        backgroundColor: action.backgroundColor,
                      }}
                      onClick={() =>
                        action.actionFn(
                          rowsData.data.filter(row => row.isSelected),
                        )
                      }
                      disabled={
                        action.authOperation &&
                        (action.authOperation &&
                          (!isAuthorized ||
                            !action.shouldEnable(rowsData.data.filter(row => row.isSelected))
                          )) ||
                        ((!isAuthorized && action.name === "Add Emergency AgSn") || (action.name === "Add Emergency AgSn" && isEmergencyAgSnDisabled && config.APPLICATION_VARIANT === APPLICATION_VARIANTS.IPMS4))
                      }
                    >
                      {action.name}
                    </Button>
                  );
                return (
                  <action.component
                    {...action.args}
                    disabled={
                      action.authOperation &&
                      (!isAuthorized ||
                        !action.shouldEnable(
                          rowsData.data.filter(row => row.isSelected),
                        ))
                    }
                  />
                );
              }}
            </AuthChecker>
          ))}

          {Object.values(fieldToFiltersSetMap).every(
            filtersSet => !filtersSet?.size,
          ) || (
            <Button
              className={clsx(styles.actionButton, styles.clearFiltersButton)}
              variant="secondary"
              disabled={Object.values(fieldToFiltersSetMap).every(
                filtersSet => !filtersSet?.size,
              )}
              onClick={() => setFieldToFiltersSetMap({})}
            >
              Clear Filters
            </Button>
          )}
          {primaryAction && (
            <AuthChecker operation={primaryAction.authOperation}>
              {isAuthorized => (
                <>
                  <Button
                    className={clsx(
                      styles.actionButton,
                      styles.primaryActionButton,
                    )}
                    variant="primary"
                    onClick={() =>
                      primaryAction.actionFn(
                        rowsData.data.filter(row => row.isSelected),
                      )
                    }
                    disabled={
                      !isAuthorized ||
                      !primaryAction.shouldEnable(
                        rowsData.data.filter(row => row.isSelected),
                      )
                    }
                    data-testid="primary-action"
                  >
                    {primaryAction.name}
                  </Button>
                  <span className={styles.date}>{aggregateSequenceDate}</span>
                </>
              )}
            </AuthChecker>
          )}
        </div>
      </div>
      <SelectionMenu
        anchorEl={customizeTableButtonRef.current}
        open={isCustomizeTableMenuOpen}
        handleClose={() => setIsCustomizeTableMenuOpen(false)}
        options={originalColumns.map(({ title }) => title)}
        currentSelections={selectedColumns.map(({ title }) => title)}
        handleChange={handleColumnSelectionChange}
        resetToDefault={() => setSelectedColumns([...defaultColumns])}
        applySelections={applyColumnsSelections}
        defaultColumns={defaultColumns.map(({ title }) => title)}
      />
      <Popover
        open={isEllipsisPopupOpen}
        anchorEl={ellipsisAnchorEl}
        onClose={closeEllipsisPopup}
        className={styles.popover}
        classes={{
          paper: styles.popoverPaper,
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {ellipsisPopupValue}
      </Popover>
      <div
        className={clsx(
          styles.copyAlert,
          showCopyAlert ? styles.visible : styles.hidden,
        )}
      >
        Text copied
      </div>
    </>
  );
};

PlanTable.propTypes = {
  classNames: PropTypes.shape({
    tableContainerClassName: PropTypes.string,
    tableClassName: PropTypes.string,
    tableRowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    tableHeadClassName: PropTypes.string,
  }),
  row: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.any.isRequired,
    }),
  ),
  rows: PropTypes.array.isRequired,
  dateColumns: PropTypes.array.isRequired,
  columns: columnsPropType,
  rowHeight: PropTypes.number,
  headerHeight: PropTypes.number,
  defaultColumns: columnsPropType,
  primaryAction: PropTypes.shape({
    name: PropTypes.string.isRequired,
    actionFn: PropTypes.func.isRequired,
  }),
  secondaryActions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      actionFn: PropTypes.func.isRequired,
      backgroundColor: PropTypes.string,
    }),
  ),
  setHasActiveEdits: PropTypes.func,
  setCount: PropTypes.func,
  isDataLoading: PropTypes.bool,
};

export default PlanTable;

function filterBySearchTexts(fieldToSearchTextMap, data, columns) {
  const fieldToSearchFnMap = columns.reduce((acc, col) => {
    if (col.searchFn) return { ...acc, [col.field]: col.searchFn };
    return acc;
  }, {});

  let filteredData = [...data];
  Object.entries(fieldToSearchTextMap).forEach(entry => {
    const [field, searchText] = entry;
    const defaultSearchFn = (fieldValue, searchValue) =>
      fieldValue.toLowerCase().startsWith(searchValue);

    const searchFn = fieldToSearchFnMap[field] || defaultSearchFn;

    filteredData = filteredData.filter(
      row => !searchText || !row[field] || searchFn(row[field], searchText),
    );
  });
  return filteredData;
}

function filterBySelectedFilters(fieldToFiltersSetMap, data, columns) {
  const fieldToFilterFnMap = columns.reduce((acc, col) => {
    if (col.filterFn) return { ...acc, [col.field]: col.filterFn };
    return acc;
  }, {});

  let filteredData = [...data];
  Object.entries(fieldToFiltersSetMap).forEach((entry, idx) => {
    const [field, filtersSet] = entry;
    const filterFn = fieldToFilterFnMap[field];

    filteredData = filteredData.filter(row => {
      if (!filterFn) return !filtersSet?.size || filtersSet.has(row[field]);
      if (!idx) {
        console.log(' [...filtersSet]', [...filtersSet]);
      }

      return (
        !filtersSet?.size ||
        [...filtersSet].some(filter => filterFn(row, filter.value))
      );
    });
  });
  return filteredData;
}

function useDeepCompareColumnArraysEffect(callback, dependencies) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(callback, dependencies.map(useDeepCompareColumnArraysMemoize));
}

function useDeepCompareColumnArraysMemoize(value) {
  const ref = useRef();
  if (!deepCompareEqualsColumnsArray(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

function deepCompareEqualsColumnsArray(oldArray, newArray) {
  if (oldArray?.length !== newArray?.length) return false;
  const zippedArray = oldArray.map((e, i) => [e, newArray[i]]);
  return zippedArray.every(tuple => tuple[0].title === tuple[1].title);
}
